/* eslint-disable react/prop-types */
/* eslint-disable camelcase */
import React, { useEffect, useState, useRef } from 'react';
import classNames from 'classnames';
import { useMediaPredicate } from 'react-media-hook';

import { media as browserMedia } from 'utilities/browser';
import { Modal, OverlayContent } from 'components/Overlay';
import { Carousel } from 'components/Carousel';
import { MediaModalButtonGroup, MediaVideo, MediaYouTube } from 'components/Media';
import { modalConfig } from 'apps/pixlee/constants';

import { UGCEvent } from '../../events';
import {
	dateMillisToDateString, decodeDescription, getSource, isInstagramSource, isTurntoSource
} from '../../helpers';
import { getPixleeContentFromAlbum, getPixleeContentFromSku } from '../../api';

import PixleeMediaModalProductList from './PixleeMediaModalProductList';
import './PixleeMediaModal.scss';

const disabledTransitionsClass = 'PixleeMediaModal__sliderWrapper--noTransitions';
let xDown = null;
let yDown = null;

const PixleeMediaModal = ({
	albumDetails,
	initialMedia,
	UGCEventTypes,
	modal,
	showShopNextPrevButtonToggleOn,
	productSku = '',
	activeSlideIndex = 0,
	showTurnToCaptions = true,
	showSocialCaptions = true
}) => {

	const isDesktop = useMediaPredicate(browserMedia.DESKTOP);
	const [media, setMedia] = useState(initialMedia);
	const [activeItemInfo, setActiveItemInfo] = useState(media.data[activeSlideIndex]);
	const [sliderOpts, setSliderOpts] = useState(modalConfig.sliderSettings);
	const [activeImageIndex, setActiveImageIndex] = useState(1);

	const sliderEl = useRef();
	const scrollDivRef = useRef();

	const carouselActiveIndex = activeSlideIndex;
	const getActiveItemCaption = (item) => {

		if (
			!item.title
			|| (!showSocialCaptions && isInstagramSource(item.source))
			|| (!showTurnToCaptions && isTurntoSource(item.source, item.email_address))
		) {

			return null;

		}

		return item.title;

	};
	const activeItemCaption = getActiveItemCaption(activeItemInfo);

	const handleSlideChange = (slideIndex) => {

		// prev
		if (slideIndex) {

			setActiveItemInfo(media.data[slideIndex]);

		}
		if (scrollDivRef.current) {

			scrollDivRef.current.scrollTo(0, 0);

		}
		// eslint-disable-next-line no-param-reassign
		slideIndex += 1;
		setActiveImageIndex(slideIndex);

		if (UGCEventTypes) {

			if (activeImageIndex < slideIndex) {

				UGCEvent({
					eventAction: UGCEventTypes.modalForwardArrowClick,
					eventLabel: slideIndex
				});

			} else {

				UGCEvent({
					eventAction: UGCEventTypes.modalBackArrowClick,
					eventLabel: slideIndex
				});

			}

		}

	};

	const handleTouchStart = (e) => {

		const firstTouch = e.touches[0] || e.originalEvent.touches[0];
		xDown = firstTouch.clientX;
		yDown = firstTouch.clientY;

	};
	const handleTouchMove = (e) => {

		if (!sliderEl.current.classList.contains(disabledTransitionsClass) || !xDown || !yDown) return;
		const xUp = e.touches[0].clientX;
		const yUp = e.touches[0].clientY;
		const xDiff = xDown - xUp;
		const yDiff = yDown - yUp;
		const isSwipeX = Math.abs(xDiff) > Math.abs(yDiff);

		if (isSwipeX) {

			sliderEl.current.classList.remove(disabledTransitionsClass);

		} else {

			sliderEl.current.classList.add(disabledTransitionsClass);

		}
		xDown = null;
		yDown = null;

	};

	const responseHandler = (response) => {

		if (response?.data && response.data.length > 0) {

			setMedia({
				...media,
				data: [...media.data, ...response.data],
				page: response.page,
				next: response.next
			});

		}

	};

	const loadMoreData = () => {

		if (productSku) {

			getPixleeContentFromSku({ ...albumDetails, page: media.page + 1, productSku }).then(responseHandler);

		} else {

			getPixleeContentFromAlbum({ ...albumDetails, page: media.page + 1 }).then(responseHandler);

		}

	};

	const showProductList = !productSku && activeItemInfo.products && activeItemInfo.products.length > 0;
	const showInstagramLink = !productSku && !!activeItemInfo.platform_link;

	useEffect(() => {

		if (activeImageIndex === media.data.length && !!media.next) {

			loadMoreData();

		}

	}, [activeImageIndex]);

	useEffect(() => {

		function handleOrientationChange() {

			sliderEl.current.classList.add(disabledTransitionsClass);

		}

		setTimeout(() => {

			setSliderOpts({
				...sliderOpts,
				customTransition: 'transform 300ms ease-in-out'
			});

		}, 500);

		window.addEventListener('orientationchange', handleOrientationChange);
		return () => {

			window.removeEventListener('orientationchange', handleOrientationChange);

		};

	}, []);

	return (
		<Modal
			className={classNames('PixleeMediaModal', {
				'PixleeMediaModal--wider': showProductList,
				'PixleeMediaModal--video': activeItemInfo.content_type === 'video'
			})}
			aria-label="Pixlee Media Modal"
			escapeExits
			greyBackground
			{...modal}
		>
			<OverlayContent>
				<main
					className={classNames('PixleeMediaModal__content', {
						PixleeMediaModal__pdp: productSku !== ''
					})}
				>
					<div
						className="PixleeMediaModal__sliderWrapper"
						ref={sliderEl}
						onTouchStart={handleTouchStart}
						onTouchMove={handleTouchMove}
					>
						<Carousel
							{...sliderOpts}
							beforeChange={() => {

								if (sliderEl.current) {

									sliderEl.current.classList.remove(disabledTransitionsClass);

								}

							}}
							afterChange={(previousSlide, { currentSlide }) => {

								setActiveItemInfo(media.data[currentSlide]);
								setActiveImageIndex(currentSlide + 1);

							}}
							customButtonGroup={(
								<MediaModalButtonGroup
									activeSlideIndex={carouselActiveIndex}
									onSlideChange={(slideIndex) => handleSlideChange(slideIndex)}
									showShopNextPrevButtonToggleOn={showShopNextPrevButtonToggleOn}
									loadMore={!!media.next}
								/>
							)}
						>
							{media.data.map((item) => {

								const {
									album_photo_id,
									source,
									source_url,
									content_type,
									thumbnail_url,
									video_settings,
									pixlee_cdn_photos: { square_medium_url }
								} = item;

								if (source === 'youtube') {

									return (
										<MediaYouTube
											key={album_photo_id}
											videoSrc={source_url}
											isActive={album_photo_id === activeItemInfo.album_photo_id}
										/>
									);

								}

								if (content_type === 'video') {

									return (
										<MediaVideo
											key={album_photo_id}
											poster={video_settings.thumbnail_url || thumbnail_url}
											source={source_url}
											controls
											tabIndex={album_photo_id === activeItemInfo.album_photo_id ? 0 : -1}
											isActive={album_photo_id === activeItemInfo.album_photo_id}
										/>
									);

								}
								// LP-51080: We are using medium url for pdp pages and for other pages square medium url.
								return (
									<img
										key={album_photo_id}
										src={
											productSku
												? item.pixlee_cdn_photos.medium_url
												: item.pixlee_cdn_photos.square_medium_url
										}
										alt=""
									/>
								);

							})}
						</Carousel>
					</div>
					{activeItemInfo && (
						<div className="PixleeMediaModal__info" ref={scrollDivRef}>
							{isDesktop && showProductList && (
								<PixleeMediaModalProductList
									activeProduct={activeItemInfo}
									products={activeItemInfo.products}
									showShopNextPrevButtonToggleOn={showShopNextPrevButtonToggleOn}
									UGCEventTypes={UGCEventTypes}
								/>
							)}
							{activeItemCaption && (
								<p
									className={classNames({
										PixleeMediaModal__instagramText: isInstagramSource(activeItemInfo.source),
										PixleeMediaModal__text: !isInstagramSource(activeItemInfo.source)
									})}
								>
									{decodeDescription(activeItemCaption)}
								</p>
							)}
							{showInstagramLink ? (
								<a
									className="PixleeMediaModal__instagramLink"
									href={activeItemInfo.platform_link}
									rel="noreferrer"
									target="_blank"
								>
									<p className="PixleeMediaModal__source">
										{activeItemInfo.user_name}
										{activeItemInfo.source && ` | ${getSource(activeItemInfo.source)}`}
									</p>
									{activeItemInfo.submitted_at !== null && (
										<p className="PixleeMediaModal__date">
											{dateMillisToDateString(activeItemInfo.submitted_at)}
										</p>
									)}
								</a>
							) : (
								<>
									<p className="PixleeMediaModal__source">
										{activeItemInfo.user_name}
										{activeItemInfo.source && ` | ${getSource(activeItemInfo.source)}`}
									</p>
									{activeItemInfo.submitted_at !== null && (
										<p className="PixleeMediaModal__date">
											{dateMillisToDateString(activeItemInfo.submitted_at)}
										</p>
									)}
								</>
							)}
							{!isDesktop && showProductList && (
								<PixleeMediaModalProductList
									activeProduct={activeItemInfo}
									products={activeItemInfo.products}
									showShopNextPrevButtonToggleOn={showShopNextPrevButtonToggleOn}
									UGCEventTypes={UGCEventTypes}
								/>
							)}
						</div>
					)}
				</main>
			</OverlayContent>
		</Modal>
	);

};

export default PixleeMediaModal;
